import { createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { baseQuery, endpoints, formatErrors } from 'utils/api/endpoints';
import { xmlFeedApi } from './xmlFeedApi';
import {
  ProductListState,
  Product,
  GetProductsParams,
  CreateProductData,
  UpdateProductData,
  UpdateStockData,
  ProductsApiResponse,
  ProductApiResponse,
  ProductStatsApiResponse,
  UploadApiResponse,
  LanguageOption,
  ProductImportLog,
  FailedUrl,
} from './types';
import { ApiResponse } from 'types/ApiResponse';

// Helper function to get language name from language code
const getLanguageName = (code: string): string => {
  const languageNames: Record<string, string> = {
    'en': 'English',
    'es': 'Spanish',
    'fr': 'French',
    'de': 'German',
    'it': 'Italian',
    'pt': 'Portuguese',
    'nl': 'Dutch',
    'ru': 'Russian',
    'ja': 'Japanese',
    'ko': 'Korean',
    'zh': 'Chinese',
    'ar': 'Arabic',
    'hi': 'Hindi',
    'bn': 'Bengali',
    'ur': 'Urdu',
    'tr': 'Turkish',
    'pl': 'Polish',
    'sv': 'Swedish',
    'da': 'Danish',
    'no': 'Norwegian',
    'fi': 'Finnish',
    'cs': 'Czech',
    'hu': 'Hungarian',
    'ro': 'Romanian',
    'bg': 'Bulgarian',
    'hr': 'Croatian',
    'sk': 'Slovak',
    'sl': 'Slovenian',
    'et': 'Estonian',
    'lv': 'Latvian',
    'lt': 'Lithuanian',
  };

  return languageNames[code.toLowerCase()] || code.toUpperCase();
};

// Initial state
export const initialState: ProductListState = {
  isModalOpen: false,
  searchQuery: '',
  currentPage: 1,
  pageSize: 20,
  selectedBrand: '',
  sortBy: 'createdAt',
  sortOrder: 'desc',
  filters: {
    isActive: true,
  },
  editProduct: null,
};

// ProductList slice
const productListSlice = createSlice({
  name: 'productList',
  initialState,
  reducers: {
    openModal: (state) => {
      state.isModalOpen = true;
    },
    closeModal: (state) => {
      state.isModalOpen = false;
      state.editProduct = null; // Clear edit product when closing modal
    },
    setEditProduct: (state, action) => {
      state.editProduct = action.payload;
    },
    clearEditProduct: (state) => {
      state.editProduct = null;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
      state.currentPage = 1; // Reset to first page on search
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // Reset to first page on page size change
    },
    setSelectedBrand: (state, action) => {
      state.selectedBrand = action.payload;
      state.currentPage = 1; // Reset to first page on filter change
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1; // Reset to first page on filter change
    },
    resetFilters: (state) => {
      state.searchQuery = '';
      state.selectedBrand = '';
      state.currentPage = 1;
      state.filters = { isActive: true };
    },
  },
});

// Real API using RTK Query
export const productListApi = createApi({
  reducerPath: 'productListApi',
  baseQuery,
  tagTypes: ['Product', 'ProductStats', 'Languages'],
  endpoints: (builder) => ({
    // Get products with pagination and filtering
    getProducts: builder.query<ProductsApiResponse, GetProductsParams>({
      query: (params) => ({
        url: endpoints.getProducts.url,
        method: endpoints.getProducts.method,
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          ...(params.search && { search: params.search }),
          ...(params.brand && { brand: params.brand }),
          ...(params.isActive !== undefined && { isActive: params.isActive }),
          ...(params.status && { status: params.status }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
          ...(params.companyId && { companyId: params.companyId }),
        },
      }),
      providesTags: ['Product'],
    }),

    // Get single product by ID
    getProductById: builder.query<ProductApiResponse, string>({
      query: (id) => ({
        url: endpoints.getProductById.url.replace(':id', id),
        method: endpoints.getProductById.method,
      }),
      providesTags: (result, error, id) => [{ type: 'Product', id }],
    }),

    // Create new product
    createProduct: builder.mutation<ProductApiResponse, CreateProductData>({
      query: (productData) => ({
        url: endpoints.createProduct.url,
        method: endpoints.createProduct.method,
        body: productData,
      }),
      invalidatesTags: ['Product', 'ProductStats'],
      transformErrorResponse: (response) => formatErrors(response),
    }),

    // Update existing product
    updateProduct: builder.mutation<ProductApiResponse, UpdateProductData>({
      query: ({ id, ...productData }) => ({
        url: endpoints.updateProduct.url.replace(':id', id),
        method: endpoints.updateProduct.method,
        body: productData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Product', id },
        'Product',
        'ProductStats',
      ],
      transformErrorResponse: (response) => formatErrors(response),
    }),

    // Delete product
    deleteProduct: builder.mutation<void, string>({
      query: (id) => ({
        url: endpoints.deleteProduct.url.replace(':id', id),
        method: endpoints.deleteProduct.method,
      }),
      invalidatesTags: ['Product', 'ProductStats'],
    }),

    // Bulk delete products
    bulkDeleteProducts: builder.mutation<{ message: string; deleted: string[]; failed: { id: string; reason: string }[] }, string[]>({
      query: (ids) => ({
        url: endpoints.bulkDeleteProducts.url,
        method: endpoints.bulkDeleteProducts.method,
        body: { ids },
      }),
      invalidatesTags: ['Product', 'ProductStats'],
    }),

    // Update product stock
    updateProductStock: builder.mutation<ProductApiResponse, { id: string; data: UpdateStockData }>({
      query: ({ id, data }) => ({
        url: endpoints.updateProductStock.url.replace(':id', id),
        method: endpoints.updateProductStock.method,
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Product', id },
        'Product',
        'ProductStats',
      ],
    }),

    // Upload product images
    uploadProductImages: builder.mutation<ProductApiResponse, { id: string; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: endpoints.uploadProductImages.url.replace(':id', id),
        method: endpoints.uploadProductImages.method,
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Product', id }],
    }),

    // Upload 3D model
    uploadProduct3DModel: builder.mutation<ProductApiResponse, { id: string; formData: FormData }>({
      query: ({ id, formData }) => ({
        url: endpoints.uploadProduct3DModel.url.replace(':id', id),
        method: endpoints.uploadProduct3DModel.method,
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Product', id }],
    }),

    // Get product statistics
    getProductStats: builder.query<ProductStatsApiResponse, { companyId?: string }>({
      query: (params) => ({
        url: endpoints.getProductStats.url,
        method: endpoints.getProductStats.method,
        params,
      }),
      providesTags: ['ProductStats'],
    }),

    // Get low stock products
    getLowStockProducts: builder.query<ProductsApiResponse, { threshold?: number; companyId?: string }>({
      query: (params) => ({
        url: endpoints.getLowStockProducts.url,
        method: endpoints.getLowStockProducts.method,
        params: {
          threshold: params.threshold || 10,
          ...(params.companyId && { companyId: params.companyId }),
        },
      }),
      providesTags: ['Product'],
    }),

    // Generic upload for 3D models and other files
    genericUpload: builder.mutation<UploadApiResponse, FormData>({
      query: (formData) => ({
        url: endpoints.genericUpload.url,
        method: endpoints.genericUpload.method,
        body: formData,
      }),
      transformErrorResponse: (response) => formatErrors(response),
    }),

    // Import products from CSV
    importProductCsv: builder.mutation<{ message: string; fileUrl: string; importId: string }, FormData>({
      query: (formData) => ({
        url: endpoints.importProductCsv.url,
        method: endpoints.importProductCsv.method,
        body: formData,
      }),
      transformErrorResponse: (response) => formatErrors(response),
      invalidatesTags: ['Product'],
    }),

    // Import product from URL
    importProductUrl: builder.mutation<ProductApiResponse, { url: string; companyId?: string }>({
      query: (data) => ({
        url: endpoints.importProductUrl.url,
        method: endpoints.importProductUrl.method,
        body: data,
      }),
      transformErrorResponse: (response) => formatErrors(response),
      invalidatesTags: ['Product', 'ProductStats'],
    }),

    // Import URLs from CSV
    importUrlsCsv: builder.mutation<ApiResponse<{ totalUrls: number }>, FormData>({
      query: (formData) => ({
        url: '/products/import-urls-csv',
        method: 'POST',
        body: formData,
      }),
      transformErrorResponse: (response) => formatErrors(response),
    }),

    // Get latest import log
    getImportLog: builder.query<ApiResponse<ProductImportLog>, { type?: 'url' | 'product' } | void>({
      query: (params) => ({
        url: endpoints.getImportStatus.url,
        method: endpoints.getImportStatus.method,
        params: params || {},
      }),
      providesTags: ['Product'],
    }),

    // Get failed URLs from latest import
    getFailedUrls: builder.query<ApiResponse<FailedUrl[]>, void>({
      query: () => ({
        url: endpoints.getFailedUrls.url,
        method: endpoints.getFailedUrls.method,
      }),
      providesTags: ['Product'],
    }),

    // Get translation data from API
    getTranslationData: builder.query<{ data: any; languages: LanguageOption[] }, void>({
      query: () => ({
        url: endpoints.translations.list.url,
        method: endpoints.translations.list.method,
      }),
      transformResponse: (response: any) => {
        const translations = response.data?.data || [];
        const languageMap = new Map<string, LanguageOption>();

        // Extract unique language codes and create language options
        translations.forEach((translation: any) => {
          if (translation.languageCode && !languageMap.has(translation.languageCode)) {
            languageMap.set(translation.languageCode, {
              code: translation.languageCode,
              name: getLanguageName(translation.languageCode),
              status: 'secondary',
              state: 'active',
            });
          }
        });

        return {
          data: translations,
          languages: Array.from(languageMap.values())
        };
      },
      providesTags: ['Languages'],
    }),
  }),
});

// Export hooks
export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductByIdQuery,
  useLazyGetProductByIdQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useBulkDeleteProductsMutation,
  useUpdateProductStockMutation,
  useUploadProductImagesMutation,
  useUploadProduct3DModelMutation,
  useGetProductStatsQuery,
  useLazyGetProductStatsQuery,
  useGetLowStockProductsQuery,
  useLazyGetLowStockProductsQuery,
  useGenericUploadMutation,
  useImportProductCsvMutation,
  useImportProductUrlMutation,
  useImportUrlsCsvMutation,
  useGetImportLogQuery,
  useLazyGetImportLogQuery,
  useGetFailedUrlsQuery,
  useLazyGetFailedUrlsQuery,
  useGetTranslationDataQuery,
  useLazyGetTranslationDataQuery,
} = productListApi;

// Export XML Feed API hooks
export const {
  useGetFeedQuery,
  useLazyGetFeedQuery,
  useFetchSchemaMutation,
  useConnectMutation,
  useUpdateMappingMutation,
  useForceSyncMutation,
  useDisconnectMutation,
} = xmlFeedApi;

// Hook to inject reducers
export const useProductListSlice = () => {
  useInjectReducer({ key: 'productList', reducer: productListSlice.reducer });
  useInjectReducer({ key: productListApi.reducerPath, reducer: productListApi.reducer });
  useInjectReducer({ key: xmlFeedApi.reducerPath, reducer: xmlFeedApi.reducer });

  return {
    actions: productListSlice.actions,
    ...productListApi.endpoints,
    useGetProductsQuery,
    useLazyGetProductsQuery,
    useGetProductByIdQuery,
    useLazyGetProductByIdQuery,
    useCreateProductMutation,
    useUpdateProductMutation,
    useDeleteProductMutation,
    useBulkDeleteProductsMutation,
    useUpdateProductStockMutation,
    useUploadProductImagesMutation,
    useUploadProduct3DModelMutation,
    useGetProductStatsQuery,
    useLazyGetProductStatsQuery,
    useGetLowStockProductsQuery,
    useLazyGetLowStockProductsQuery,
    useGenericUploadMutation,
    useImportProductCsvMutation,
    useImportProductUrlMutation,
    useImportUrlsCsvMutation,
    useGetImportLogQuery,
    useLazyGetImportLogQuery,
    useGetFailedUrlsQuery,
    useLazyGetFailedUrlsQuery,
    useGetTranslationDataQuery,
    useLazyGetTranslationDataQuery,
  };
};

export default productListSlice.reducer;
